import React, { Key, useContext, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import { Modal as AntModal } from 'antd';
import {
  Button,
  Dropdown,
  EllipsisSpan,
  Form,
  Modal,
  Paging,
  Select,
  Table,
  Tag,
  useSearchParams,
  useUpdate,
} from '@maxtropy/components';
import { Space } from 'antd';
import { DistributeStatus, StatusType } from '@/pages/PowerLightingControl/utils';
import { isNil } from 'lodash-es';
import { LoadingOutlined } from '@ant-design/icons';
import {
  apiV2GgdBatchOperateStatusPost,
  apiV2GgdDistributeTimeTaskPost,
  apiV2LightmapDeviceList2Post,
  apiV2LightmapDevicePage2Post,
  V2GgdDeviceListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import ScheduledTaskModal from '@/pages/PowerLightingControl/components/ScheduledTaskModal';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';
import CardBox from '../../../CardBox';

export type DeviceItem = Exclude<V2GgdDeviceListPostResponse['list'], undefined>[number];

const getFullScreenPopupContainer = () => document.getElementById('full_screen_box') ?? document.body;

const RightBox = () => {
  const { scale } = useContext(ShareDataWrapperContext);

  const [modalApi, modalContextHolder] = AntModal.useModal();
  const [update, updateFn] = useUpdate();
  const pagingInfo = useSearchParams(50);

  const { pageOffset, pageSize, setTotalCount } = pagingInfo;

  const [deviceId, setDeviceId] = useState<number>();
  const [selectedRows, setSelectedRows] = useState<DeviceItem[]>([]);
  const [currentId, setCurrentId] = useState<number>();
  const [currentDeviceId, setCurrentDeviceId] = useState<number>();
  const [showScheduledTaskModal, setShowScheduledTaskModal] = useState<boolean>(false);

  const { data, loading } = useRequest(
    () =>
      apiV2LightmapDevicePage2Post({
        deviceId,
        page: pageOffset,
        size: pageSize,
      }).then(res => {
        setTotalCount(res.total ?? 0);
        return res.list;
      }),
    {
      refreshDeps: [deviceId, pageOffset, pageSize, update],
    }
  );
  const { data: deviceList } = useRequest(async () => {
    const res = await apiV2LightmapDeviceList2Post({});
    return res.list;
  });

  const deviceOptions = useMemo(() => {
    return (deviceList ?? []).map(item => ({
      label: item.deviceName,
      value: item.deviceId,
    }));
  }, [deviceList]);

  const makeLoadingModel = () =>
    modalApi.info({
      className: 'loading-modal',
      title: '正在执行',
      content: (
        <div className="loading-modal-content">
          <div className="loading-modal-icon">
            <LoadingOutlined />
          </div>
          <div className="loading-modal-txt">正在执行中</div>
        </div>
      ),
    });

  const remoteControl = (deviceIds: number[], deviceNames: string[], status: StatusType) => {
    if (!deviceIds.length) return;
    Modal.confirm({
      title: (
        <span style={{ color: 'var(--mx-text-base-color)' }}>
          确定要下发<span style={{ color: '#E64242' }}>{status === StatusType.CLOSED ? '远程开启' : '远程关闭'}</span>
          指令到{deviceNames.join('、')}设备吗？
        </span>
      ),
      okText: '确定',

      onOk() {
        const modal = makeLoadingModel();
        setSelectedRows([]);
        apiV2GgdBatchOperateStatusPost({ deviceIds, status })
          .then(_ => {
            updateFn();
          })
          .finally(() => {
            modal.destroy();
          });
      },
    });
  };
  const distributeTask = (id: number) => {
    if (isNil(id)) return;
    apiV2GgdDistributeTimeTaskPost({
      id: id!,
    }).then(res => {
      updateFn();
    });
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (_: Key[], selectedRows: DeviceItem[]) => {
      setSelectedRows(selectedRows);
    },
    selectedRowKeys: selectedRows?.map(item => item.deviceId as Key) ?? [],
  };

  const buildColumns = [
    {
      title: '控制设备名称',
      dataIndex: 'deviceName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '闸位状态',
      dataIndex: 'intStatus',
      ellipsis: { showTitle: true },
      render: (v: number) =>
        isNil(v) ? (
          '--'
        ) : v === 0 ? (
          <Tag border="solid" color="default">
            关闭
          </Tag>
        ) : v === 1 ? (
          <Tag border="solid" color="success">
            开启
          </Tag>
        ) : (
          <Tag border="solid" color="warning">
            离线
          </Tag>
        ),
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 88,
      render: (value: any, record: any) => {
        return (
          <Space size={16}>
            <Dropdown
              type="link"
              getPopupContainer={getFullScreenPopupContainer}
              menu={{
                items: [
                  {
                    key: '1',
                    label: '远程关闭',
                    disabled:
                      isNil(record.status) || record.status === StatusType.OPEN || record.status === StatusType.PENDING,
                    onClick: () => {
                      remoteControl([record.deviceId], [record.deviceName], StatusType.OPEN);
                    },
                  },
                  {
                    key: '2',
                    label: '远程开启',
                    disabled:
                      isNil(record.status) ||
                      record.status === StatusType.CLOSED ||
                      record.status === StatusType.PENDING,
                    onClick: () => {
                      remoteControl([record.deviceId], [record.deviceName], StatusType.CLOSED);
                    },
                  },
                  {
                    key: '3',
                    label: '定时任务',
                    disabled:
                      isNil(record.status) ||
                      !record.canSetTimeTask ||
                      record.taskDistributeStatus === DistributeStatus.PENDING ||
                      record.taskDistributeStatus === DistributeStatus.FINISH,
                    onClick: () => {
                      setCurrentId(record.id);
                      setCurrentDeviceId(record.deviceId);
                      setShowScheduledTaskModal(true);
                    },
                  },
                  {
                    key: '4',
                    label: '下发定时任务',
                    disabled:
                      isNil(record.status) ||
                      !record.canSetTimeTask ||
                      record.taskDistributeStatus === DistributeStatus.PENDING ||
                      record.taskDistributeStatus === DistributeStatus.FINISH,
                    onClick: () => {
                      distributeTask(record.id);
                    },
                  },
                ],
              }}
            >
              控制
            </Dropdown>
          </Space>
        );
      },
    },
  ];
  return (
    <div className={styles.right_wrapper}>
      <CardBox
        title="高杆灯照明控制"
        extraContent={
          <Button
            type="link"
            onClick={() => {
              window.open(`/power/lighting/control`, '_blank');
            }}
          >
            详情{'>>'}
          </Button>
        }
      >
        <Form.Item label="控制设备名称" style={{ margin: '16px 0' }}>
          <Select
            placeholder="请选择"
            optionFilterProp="label"
            showSearch
            allowClear
            options={deviceOptions}
            onChange={(value: number) => {
              setDeviceId(value);
              setSelectedRows([]);
            }}
            style={{ width: 280 }}
          />
        </Form.Item>
        <Space size={8} style={{ marginBottom: 12 }}>
          <div
            className={styles.batch_btn}
            onClick={() =>
              remoteControl(
                (selectedRows ?? []).map(i => i.deviceId!),
                (selectedRows ?? []).map(i => i.deviceName!),
                StatusType.CLOSED
              )
            }
          >
            批量开启
          </div>
          <div
            className={styles.batch_btn}
            onClick={() =>
              remoteControl(
                (selectedRows ?? []).map(i => i.deviceId!),
                (selectedRows ?? []).map(i => i.deviceName!),
                StatusType.OPEN
              )
            }
          >
            批量关闭
          </div>
        </Space>
        <Table
          loading={loading}
          rowKey="deviceId"
          columns={buildColumns}
          dataSource={data}
          scroll={{ y: `calc(100vh - ${Math.ceil(scale!) * 500}px)` }}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
        />
        <Paging pagingInfo={pagingInfo} showSizeChanger={false} />
        {showScheduledTaskModal && (
          <ScheduledTaskModal
            id={currentId}
            deviceId={currentDeviceId}
            open={showScheduledTaskModal}
            setOpen={setShowScheduledTaskModal}
            updateFn={updateFn}
          />
        )}
        {modalContextHolder}
      </CardBox>
    </div>
  );
};

export default RightBox;
